import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import PageTitle from "../../../layouts/PageTitle";
import { addStoreEntryApi } from "../../../../services/inventory";
import { StockBulkUploadModal } from "./BulkUploadModals";
import MultiEntryForm from "./MultiEntryForm";
import {
  appApi,
  useGetSkusQuery,
  useGetIssuedToOptionsQuery,
  useGetStoreTransactionsQuery,
  useGetVehiclesQuery,
} from "../../../../store/appApi";
import { selectUser, selectUserRole } from "../../../../store/authSlice";
import { Link } from "react-router-dom";
import Select from "react-select";
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const EMPTY_FORM = {
  date: todayKey(),
  skuId: "",
  entryType: "add",
  qty: "",
  price: "",
  supplierId: "",
  recipientId: "",
  recipientType: "",
  invoiceNo: "",
  truckNo: "",
  isUsedInVehicle: false,
  vehicleId: "",
};

const ENTRY_LABELS = {
  add: "Add to Stock",
  issued: "Issue from Stock",
  both: "Add & Issue (direct delivery)",
};

const IssueStock = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";

  const [form, setForm] = useState(EMPTY_FORM);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [multiMode, setMultiMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // ── Remote data ──────────────────────────────────────────────────────────────
  const { data: skuData, isLoading: skuLoading } = useGetSkusQuery();
  const skus = Array.isArray(skuData?.skus) ? skuData.skus : [];
  // searchable by item name (and unit) in the dropdown
  const skuOptions = skus.map((sku) => ({
    value: sku._id,
    label: `${sku.skuName} (${sku.measurement})`,
  }));

  const { data: optionsData, isLoading: optionsLoading } =
    useGetIssuedToOptionsQuery();
  const issuedToOptions = Array.isArray(optionsData?.options)
    ? optionsData.options
    : [];

  const { data: vehicleData, isLoading: vehicleLoading } = useGetVehiclesQuery({
    limit: 100,
  });
  const activeVehicles = Array.isArray(vehicleData?.data)
    ? vehicleData.data.filter((v) => v.status === true)
    : [];

  const {
    data: txnData,
    isLoading: txnLoading,
    isFetching: txnFetching,
  } = useGetStoreTransactionsQuery({ skuId: form.skuId, limit: 5000 });
  const transactions = Array.isArray(txnData?.stocks) ? txnData.stocks : [];
  const listLoading = txnLoading || txnFetching;

  // Compute current balance for selected SKU
  const currentBalance =
    transactions.length > 0 ? (transactions[0].balance ?? 0) : 0;
  const selectedSkuUnit =
    skus.find((s) => s._id === form.skuId)?.measurement || "";

  // "Add & Issue": stock arrives from a supplier and goes straight to a contractor
  const isAddPart = form.entryType === "add" || form.entryType === "both";
  const isIssuePart = form.entryType === "issued" || form.entryType === "both";

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;

    setForm((prev) => {
      const next = { ...prev, [name]: val };
      // Reset vehicle on uncheck
      if (name === "isUsedInVehicle" && !checked) next.vehicleId = "";
      // Reset issuedTo fields when switching entryType
      if (name === "entryType") {
        next.recipientId = "";
        next.recipientType = "";
        next.supplierId = "";
        next.price = "";
        next.isUsedInVehicle = false;
        next.vehicleId = "";
      }
      return next;
    });

    if (name === "recipientId") {
      const selected = issuedToOptions.find((o) => o._id === value);
      setForm((prev) => ({
        ...prev,
        recipientId: value,
        recipientType: selected?.type || "",
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.skuId) e.skuId = "Select an item.";
    if (!form.qty || Number(form.qty) <= 0) e.qty = "Enter a valid quantity.";
    if (isAddPart && !form.supplierId) e.supplierId = "Select a supplier.";
    if (isIssuePart && !form.recipientId) e.recipientId = "Select a recipient.";
    if (form.isUsedInVehicle && !form.vehicleId)
      e.vehicleId = "Select a vehicle.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      await addStoreEntryApi({
        skuId: form.skuId,
        qty: Number(form.qty),
        entryType: form.entryType,
        invoiceNo: form.invoiceNo || undefined,
        truckNo: form.truckNo.trim() || undefined,
        date: form.date || undefined,
        supplierId: isAddPart ? form.supplierId : undefined,
        price: isAddPart && form.price ? Number(form.price) : undefined,
        recipientId: isIssuePart ? form.recipientId : undefined,
        recipientType: isIssuePart ? form.recipientType : undefined,
        isUsedInVehicle: form.entryType === "issued" && form.isUsedInVehicle,
        vehicleId:
          form.entryType === "issued" && form.isUsedInVehicle
            ? form.vehicleId
            : undefined,
      });
      dispatch(appApi.util.invalidateTags(["StoreTransactions"]));
      setForm((prev) => ({ ...EMPTY_FORM, skuId: prev.skuId }));
      setErrors({});
      Swal.fire({
        icon: "success",
        title: "Entry Saved",
        text: `Stock ${form.entryType === "add" ? "added" : form.entryType === "both" ? "received and issued" : "issued"} successfully.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to save entry.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";
  const fmtNum = (n) => (n != null ? Number(n).toLocaleString("en-IN") : "—");

  const projectedBalance =
    form.skuId && form.qty && form.entryType !== "both"
      ? form.entryType === "add"
        ? currentBalance + Number(form.qty)
        : currentBalance - Number(form.qty)
      : null;
  return (
    <>
      <PageTitle
        activeMenu="Store Transactions"
        motherMenu="Inventory / Store"
      />
      {isSuperAdmin && (
        <StockBulkUploadModal
          show={bulkOpen}
          onHide={() => setBulkOpen(false)}
          onDone={() => dispatch(appApi.util.invalidateTags(["StoreTransactions"]))}
        />
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
        className="mb-4"
      >
        {" "}
        <Link to="/inventory/store" className="btn btn-outline-primary btn-sm">
          All Entries
        </Link>
      </div>
      <div style={{ justifyContent: "center" }} className="row">
        <div className={multiMode ? "col-12" : "col-xl-6 col-lg-5"}>
          <div className="card" style={{ position: "sticky", top: 0 }}>
            <div className="card-header d-flex align-items-center gap-2">
              <h5 className="card-title mb-0">
                <i className="fa fa-exchange me-2 text-primary" />
                New Entry
              </h5>
              <button
                type="button"
                className={`btn btn-sm ms-auto ${multiMode ? "btn-secondary" : "btn-outline-primary"}`}
                onClick={() => setMultiMode((v) => !v)}
              >
                <i className={`fa fa-${multiMode ? "arrow-left" : "list"} me-1`} />
                {multiMode ? "Single Entry" : "Multiple Entries"}
              </button>
              {isSuperAdmin && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setBulkOpen(true)}
                >
                  <i className="fa fa-upload me-1" /> Bulk Upload
                </button>
              )}
            </div>
            <div className="card-body">
              {multiMode && (
                <>
                  <div className="d-flex gap-3 mb-3">
                    {["add", "issued", "both"].map((t) => (
                      <div key={t} className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          id={`mt-${t}`}
                          checked={form.entryType === t}
                          onChange={() =>
                            setForm((prev) => ({ ...prev, entryType: t }))
                          }
                        />
                        <label className="form-check-label" htmlFor={`mt-${t}`}>
                          {ENTRY_LABELS[t]}
                        </label>
                      </div>
                    ))}
                  </div>
                  <MultiEntryForm
                    entryType={form.entryType}
                    skuOptions={skuOptions}
                    issuedToOptions={issuedToOptions}
                    skuUnits={Object.fromEntries(skus.map((k) => [k._id, k.measurement]))}
                    onSaved={() =>
                      dispatch(appApi.util.invalidateTags(["StoreTransactions"]))
                    }
                  />
                </>
              )}
              <form
                onSubmit={handleSubmit}
                noValidate
                style={multiMode ? { display: "none" } : undefined}
              >
                {/* Entry Type */}
                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">Entry Type</label>
                  <div className="d-flex gap-3">
                    {["add", "issued", "both"].map((t) => (
                      <div key={t} className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          id={`et-${t}`}
                          name="entryType"
                          value={t}
                          checked={form.entryType === t}
                          onChange={handleChange}
                          disabled={loading}
                        />
                        <label
                          className="form-check-label text-capitalize"
                          htmlFor={`et-${t}`}
                        >
                          {ENTRY_LABELS[t]}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* SKU */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="skuId">
                    Item (SKU) <span className="text-danger">*</span>
                  </label>
                  <Select
                    inputId="skuId"
                    classNamePrefix="sku-select"
                    placeholder={skuLoading ? "Loading…" : "Search or select item…"}
                    noOptionsMessage={() => "No matching item"}
                    isClearable
                    isSearchable
                    isDisabled={loading || skuLoading}
                    options={skuOptions}
                    value={skuOptions.find((o) => o.value === form.skuId) || null}
                    onChange={(opt) =>
                      handleChange({
                        target: {
                          name: "skuId",
                          value: opt ? opt.value : "",
                          type: "select-one",
                        },
                      })
                    }
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                      control: (base, state) => ({
                        ...base,
                        minHeight: 42,
                        borderColor: errors.skuId ? "#dc3545" : base.borderColor,
                      }),
                    }}
                  />
                  {errors.skuId && (
                    <div className="invalid-feedback d-block">{errors.skuId}</div>
                  )}
                  {form.skuId && (
                    <div className="form-text">
                      Current balance:{" "}
                      <strong>
                        {fmtNum(currentBalance)} {selectedSkuUnit}
                      </strong>
                    </div>
                  )}
                </div>

                {/* Quantity */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="qty">
                    Quantity <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <input
                      id="qty"
                      name="qty"
                      type="number"
                      min="1"
                      className={`form-control ${errors.qty ? "is-invalid" : ""}`}
                      placeholder="0"
                      value={form.qty}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    {selectedSkuUnit && (
                      <span className="input-group-text text-muted">
                        {selectedSkuUnit}
                      </span>
                    )}
                  </div>
                  {errors.qty && (
                    <div className="invalid-feedback d-block">{errors.qty}</div>
                  )}
                  {projectedBalance !== null && (
                    <div
                      className={`form-text fw-semibold ${
                        projectedBalance < 0 ? "text-danger" : "text-success"
                      }`}
                    >
                      New balance: {fmtNum(projectedBalance)} {selectedSkuUnit}
                      {projectedBalance < 0 && " ⚠ Insufficient stock"}
                    </div>
                  )}
                </div>

                {/* ── Add-specific: Supplier + Price ─────────────────────────── */}
                {isAddPart && (
                  <>
                    <div className="form-group mb-3">
                      <label className="form-label" htmlFor="supplierId">
                        Supplier <span className="text-danger">*</span>
                      </label>
                      <select
                        id="supplierId"
                        name="supplierId"
                        className={`form-control ${errors.supplierId ? "is-invalid" : ""}`}
                        value={form.supplierId}
                        onChange={handleChange}
                        disabled={loading || optionsLoading}
                      >
                        <option value="">— Select Supplier —</option>
                        {issuedToOptions
                          .filter((o) => o.type === "supplier")
                          .map((o) => (
                            <option key={o._id} value={o._id}>
                              {o.name}
                            </option>
                          ))}
                      </select>
                      {errors.supplierId && (
                        <div className="invalid-feedback">
                          {errors.supplierId}
                        </div>
                      )}
                    </div>

                    <div className="form-group mb-3">
                      <label className="form-label" htmlFor="price">
                        Total Price (₹){" "}
                        <span className="text-muted small fw-normal">
                          (optional)
                        </span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <input
                          id="price"
                          name="price"
                          type="number"
                          min="0"
                          className="form-control"
                          placeholder="0.00"
                          value={form.price}
                          onChange={handleChange}
                          disabled={loading}
                        />
                      </div>
                      {form.price && form.qty && Number(form.qty) > 0 && (
                        <div className="form-text">
                          ₹ {(Number(form.price) / Number(form.qty)).toFixed(2)}{" "}
                          per {selectedSkuUnit || "unit"}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* ── Issue-specific: Recipient + Vehicle ─────────────────────── */}
                {isIssuePart && (
                  <>
                    <div className="form-group mb-3">
                      <label className="form-label" htmlFor="recipientId">
                        Issued To <span className="text-danger">*</span>
                      </label>
                      <select
                        id="recipientId"
                        name="recipientId"
                        className={`form-control ${errors.recipientId ? "is-invalid" : ""}`}
                        value={form.recipientId}
                        onChange={handleChange}
                        disabled={loading || optionsLoading}
                      >
                        <option value="">
                          — Select Contractor / Supplier —
                        </option>
                        {issuedToOptions.map((o) => (
                          <option key={o._id} value={o._id}>
                            {o.name} — {o.role}
                          </option>
                        ))}
                      </select>
                      {errors.recipientId && (
                        <div className="invalid-feedback">
                          {errors.recipientId}
                        </div>
                      )}
                    </div>

                    {/* Used in Vehicle? (plain issue only) */}
                    {form.entryType === "issued" && (
                    <div className="form-group mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="isUsedInVehicle"
                          name="isUsedInVehicle"
                          checked={form.isUsedInVehicle}
                          onChange={handleChange}
                          disabled={loading}
                        />
                        <label
                          className="form-check-label fw-semibold"
                          htmlFor="isUsedInVehicle"
                        >
                          Used in Vehicle?
                        </label>
                      </div>
                    </div>
                    )}

                    {form.entryType === "issued" && form.isUsedInVehicle && (
                      <div className="form-group mb-3">
                        <label className="form-label" htmlFor="vehicleId">
                          Vehicle <span className="text-danger">*</span>
                        </label>
                        <select
                          id="vehicleId"
                          name="vehicleId"
                          className={`form-control ${errors.vehicleId ? "is-invalid" : ""}`}
                          value={form.vehicleId}
                          onChange={handleChange}
                          disabled={loading || vehicleLoading}
                        >
                          <option value="">
                            {vehicleLoading ? "Loading…" : "— Select Vehicle —"}
                          </option>
                          {activeVehicles.map((v) => (
                            <option key={v._id} value={v._id}>
                              {v.vehicleNumber} · {v.vehicleMake}{" "}
                              {v.vehicleModel}
                            </option>
                          ))}
                        </select>
                        {errors.vehicleId && (
                          <div className="invalid-feedback">
                            {errors.vehicleId}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Entry date — any past date is allowed */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="entryDate">
                    Entry Date
                  </label>
                  <input
                    id="entryDate"
                    name="date"
                    type="date"
                    className="form-control"
                    value={form.date}
                    max={todayKey()}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>

                {/* Invoice No */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="invoiceNo">
                    {form.entryType === "add"
                      ? "Bill No."
                      : "Voucher No. (V. No)"}{" "}
                    <span className="text-muted small fw-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    id="invoiceNo"
                    name="invoiceNo"
                    type="text"
                    className="form-control"
                    placeholder="e.g. INV-2024-001"
                    value={form.invoiceNo}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>

                {/* Truck No */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="truckNo">
                    Truck No.{" "}
                    <span className="text-muted small fw-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    id="truckNo"
                    name="truckNo"
                    type="text"
                    className="form-control text-uppercase"
                    placeholder="e.g. TS09AB1234"
                    value={form.truckNo}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>

                {/* Issued By (read-only) */}
                <div className="form-group mb-4">
                  <label className="form-label">Issued By</label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentUser?.name || "—"}
                    readOnly
                    disabled
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={
                    loading ||
                    (projectedBalance !== null && projectedBalance < 0)
                  }
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <i
                        className={`fa fa-${form.entryType === "add" ? "plus" : "minus"} me-2`}
                      />
                      {form.entryType === "add"
                        ? "Add to Stock"
                        : form.entryType === "both"
                          ? "Add & Issue"
                          : "Issue Stock"}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default IssueStock;
